/**
 * App Catalogue — Apps list page.
 *
 * The list/search/enable-disable/delete experience for every app manifest in
 * the CCMS mobile constellation. Lives under the App Catalogue domain app at
 * `/admin/app-catalogue/apps`; New/Edit navigate to the nested `apps/new` and
 * `apps/edit/:appId` routes.
 *
 * Backed by the `CCMS.Api.ApplicationCatalogue` service via the SDK's
 * `ApplicationCatalogueService`.
 */

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  IconPlus,
  IconSearch,
  IconRefresh,
  IconEdit,
  IconTrash,
  IconApps,
  IconCircleCheck,
  IconCircleOff,
  IconExternalLink,
} from '@tabler/icons-react';
import {
  Stack,
  Card,
  Group,
  Button,
  Text,
  Title,
  TextInput,
  Badge,
  Paper,
  SimpleGrid,
  ThemeIcon,
  Tooltip,
  ActionIcon,
  Alert,
  Switch,
  Avatar,
  toast,
} from '@/components/ui';
import { ActionIcon as MantineActionIcon } from '@mantine/core';
import { ConfirmDeleteModal } from '@/components/ui/overlays';
import { FadeIn, HoverLift } from '@/components/animations';
import { useApplicationCatalogueService } from '@actschurch/ccms-sdk';
import type { AppManifest } from '@actschurch/ccms-sdk';
import { ADMIN_ROUTES } from '@/config/routes';

const CATALOGUE_QUERY_KEY = ['admin', 'app-catalogue', 'all'];

export default function AppsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const catalogueService = useApplicationCatalogueService();

  const [searchQuery, setSearchQuery] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<AppManifest | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [togglingId, setTogglingId] = useState<string | null>(null);

  const {
    data: apps = [],
    isLoading,
    error,
    isFetching,
  } = useQuery({
    queryKey: CATALOGUE_QUERY_KEY,
    queryFn: () => catalogueService.getAllApps(),
  });

  const stats = useMemo(
    () => ({
      total: apps.length,
      enabled: apps.filter((a) => a.enabled).length,
      disabled: apps.filter((a) => !a.enabled).length,
    }),
    [apps]
  );

  const rows = useMemo(() => {
    if (!searchQuery) return apps;
    const q = searchQuery.toLowerCase();
    return apps.filter(
      (a) =>
        a.appId.toLowerCase().includes(q) ||
        a.displayName.toLowerCase().includes(q) ||
        a.shortName.toLowerCase().includes(q) ||
        (a.metadata?.ownerTeam ?? '').toLowerCase().includes(q)
    );
  }, [apps, searchQuery]);

  const handleToggle = async (app: AppManifest) => {
    setTogglingId(app.appId);
    try {
      await catalogueService.toggleApp(app.appId, !app.enabled);
      await queryClient.invalidateQueries({ queryKey: CATALOGUE_QUERY_KEY });
      toast.success(`${app.displayName} ${app.enabled ? 'disabled' : 'enabled'}`);
    } catch (err: any) {
      toast.error(err?.message || 'Failed to toggle app');
    } finally {
      setTogglingId(null);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await catalogueService.deleteApp(deleteTarget.appId);
      await queryClient.invalidateQueries({ queryKey: CATALOGUE_QUERY_KEY });
      toast.success(`${deleteTarget.displayName} deleted`);
      setDeleteTarget(null);
    } catch (err: any) {
      toast.error(err?.message || 'Failed to delete app');
    } finally {
      setDeleting(false);
    }
  };

  if (error) {
    return (
      <Stack gap="lg">
        <Title order={2}>Apps</Title>
        <Alert color="red" title="Service Unavailable">
          <Text size="sm">
            {error instanceof Error ? error.message : 'Failed to load the app catalogue.'}
          </Text>
          <Button
            variant="light"
            color="red"
            size="xs"
            mt="sm"
            onClick={() => queryClient.invalidateQueries({ queryKey: CATALOGUE_QUERY_KEY })}
          >
            Retry
          </Button>
        </Alert>
      </Stack>
    );
  }

  return (
    <FadeIn>
      <Stack gap="lg">
        {/* Header */}
        <Group justify="space-between">
          <div>
            <Title order={2}>Apps</Title>
            <Text c="dimmed" size="sm">
              Manage the CCMS mobile constellation — Hub and spoke app manifests.
            </Text>
          </div>
          <Group>
            <Button
              variant="subtle"
              leftSection={<IconRefresh size={16} />}
              onClick={() => queryClient.invalidateQueries({ queryKey: CATALOGUE_QUERY_KEY })}
              loading={isFetching}
              size="sm"
            >
              Refresh
            </Button>
            <Button
              leftSection={<IconPlus size={16} />}
              onClick={() => navigate(ADMIN_ROUTES.APP_CATALOGUE_NEW)}
            >
              New App
            </Button>
          </Group>
        </Group>

        {/* Stats */}
        <SimpleGrid cols={{ base: 1, sm: 3 }}>
          <Paper withBorder p="md" radius="md">
            <Group>
              <ThemeIcon size="lg" radius="md" variant="light" color="blue">
                <IconApps size={20} />
              </ThemeIcon>
              <div>
                <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                  Total Apps
                </Text>
                <Text size="xl" fw={700}>
                  {stats.total}
                </Text>
              </div>
            </Group>
          </Paper>
          <Paper withBorder p="md" radius="md">
            <Group>
              <ThemeIcon size="lg" radius="md" variant="light" color="green">
                <IconCircleCheck size={20} />
              </ThemeIcon>
              <div>
                <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                  Enabled
                </Text>
                <Text size="xl" fw={700}>
                  {stats.enabled}
                </Text>
              </div>
            </Group>
          </Paper>
          <Paper withBorder p="md" radius="md">
            <Group>
              <ThemeIcon size="lg" radius="md" variant="light" color="gray">
                <IconCircleOff size={20} />
              </ThemeIcon>
              <div>
                <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                  Disabled
                </Text>
                <Text size="xl" fw={700}>
                  {stats.disabled}
                </Text>
              </div>
            </Group>
          </Paper>
        </SimpleGrid>

        {/* Search + list */}
        <Card withBorder padding="md" radius="md">
          <Stack gap="md">
            <TextInput
              placeholder="Search by app id, name, or owner team..."
              leftSection={<IconSearch size={16} />}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.currentTarget.value)}
            />

            {isLoading ? (
              <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }}>
                {[1, 2, 3].map((i) => (
                  <Card key={i} withBorder padding="md" radius="md">
                    <div
                      style={{
                        height: 96,
                        background: 'var(--mantine-color-gray-1)',
                        borderRadius: 'var(--mantine-radius-sm)',
                      }}
                    />
                  </Card>
                ))}
              </SimpleGrid>
            ) : rows.length === 0 ? (
              <Paper withBorder p="xl" ta="center">
                <Text c="dimmed">
                  {apps.length === 0
                    ? 'No app manifests in the catalogue yet.'
                    : 'No apps match your search.'}
                </Text>
              </Paper>
            ) : (
              <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }}>
                {rows.map((app) => (
                  <HoverLift key={app.appId} lift={4}>
                    <Card withBorder padding="md" radius="md" h="100%">
                      <Stack gap="sm" h="100%">
                        <Group justify="space-between" wrap="nowrap" align="flex-start">
                          <Group gap="sm" wrap="nowrap">
                            <Avatar
                              src={app.iconUrl || undefined}
                              radius="md"
                              size="md"
                              style={{
                                backgroundColor: app.colors?.primary || undefined,
                              }}
                            >
                              {app.shortName?.charAt(0) ?? '?'}
                            </Avatar>
                            <div>
                              <Text fw={600} lineClamp={1}>
                                {app.displayName}
                              </Text>
                              <Text size="xs" c="dimmed">
                                {app.appId}
                              </Text>
                            </div>
                          </Group>
                          <Badge
                            size="sm"
                            variant="light"
                            color={app.enabled ? 'green' : 'gray'}
                          >
                            {app.enabled ? 'Enabled' : 'Disabled'}
                          </Badge>
                        </Group>

                        {app.description && (
                          <Text size="sm" c="dimmed" lineClamp={2}>
                            {app.description}
                          </Text>
                        )}

                        <Group gap={6} wrap="wrap">
                          <Badge size="xs" variant="outline" color="blue">
                            v{app.minAppVersion}+
                          </Badge>
                          {(app.categories as string[])?.slice(0, 3).map((c) => (
                            <Badge key={c} size="xs" variant="light" color="violet">
                              {c}
                            </Badge>
                          ))}
                          {app.audience && app.audience !== 'staff' && (
                            <Badge size="xs" variant="light" color="orange">
                              {app.audience}
                            </Badge>
                          )}
                        </Group>

                        <div style={{ flex: 1 }} />

                        <Group justify="space-between" align="center">
                          <Switch
                            size="sm"
                            label={app.enabled ? 'Enabled' : 'Disabled'}
                            checked={app.enabled}
                            disabled={togglingId === app.appId}
                            onChange={() => handleToggle(app)}
                          />
                          <Group gap={4} wrap="nowrap">
                            {app.webUrl && (
                              <Tooltip label="Open web app">
                                <MantineActionIcon
                                  variant="subtle"
                                  size="sm"
                                  color="gray"
                                  component="a"
                                  href={app.webUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  <IconExternalLink size={14} />
                                </MantineActionIcon>
                              </Tooltip>
                            )}
                            <Tooltip label="Edit">
                              <ActionIcon
                                variant="subtle"
                                size="sm"
                                onClick={() =>
                                  navigate(ADMIN_ROUTES.APP_CATALOGUE_EDIT(app.appId))
                                }
                              >
                                <IconEdit size={14} />
                              </ActionIcon>
                            </Tooltip>
                            <Tooltip label="Delete">
                              <ActionIcon
                                variant="subtle"
                                color="red"
                                size="sm"
                                onClick={() => setDeleteTarget(app)}
                              >
                                <IconTrash size={14} />
                              </ActionIcon>
                            </Tooltip>
                          </Group>
                        </Group>
                      </Stack>
                    </Card>
                  </HoverLift>
                ))}
              </SimpleGrid>
            )}
          </Stack>
        </Card>

        {/* Delete confirmation */}
        <ConfirmDeleteModal
          opened={!!deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onConfirm={handleDelete}
          loading={deleting}
          title="Delete App Manifest"
          message={`Are you sure you want to delete "${
            deleteTarget?.displayName ?? 'this app'
          }" from the catalogue? Installed spokes will keep working until next cold-start. This cannot be undone.`}
        />
      </Stack>
    </FadeIn>
  );
}
